import { Box, Button } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Tabs, { TabHeader, TabPanels } from 'components/Tabs';
import Title from 'components/Title';
import { routePaths } from 'global/routePaths';
import { ICustomFieldItem } from 'hooks/customField/useFetchPaginatedCustomFields';
import { ISaveJobData, useSaveJob } from 'hooks/jobs/useSaveJob';
import { useJobsTasksStore } from 'store/useJobsTasksStore';
import { transformCustomFieldsToPostData } from 'utils/customFieldsUtils';
import toaster from 'utils/toaster';

import JobFormDetails from '../JobFormDetails';
import JobFormGeoFence from '../JobFormGeoFence';
import JobsCustomFields from '../JobsCustomFields';
import { initialJobFormData, jobFormValidationSchema } from '../schema';

interface IJobFormTabsProps {
  formData?: typeof initialJobFormData;
  customFieldsData: Array<ICustomFieldItem> | null;
}

const JobFormTabs: React.FC<IJobFormTabsProps> = ({ formData = initialJobFormData, customFieldsData }) => {
  const { t } = useTranslation(['jobsTasks', 'common']);
  const tabPanels = [
    { panel: <JobFormDetails /> },
    { panel: <JobFormGeoFence /> },
    ...(customFieldsData?.length ? [{ panel: <JobsCustomFields customFieldsData={customFieldsData} /> }] : []),
  ];

  const tabHeaders = [
    { label: t('common:details') },
    { label: t('jobsTasks:locationDetails') },
    ...(customFieldsData?.length ? [{ label: t('common:customFields') }] : []),
  ];

  const navigate = useNavigate();

  const { status, mutateAsync } = useSaveJob();
  const { jobsTasksHasRedirected } = useJobsTasksStore(state => state);

  useEffect(() => {
    if (status === 'success') {
      jobsTasksHasRedirected();
      navigate(routePaths.jobsAndTasks.jobs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleFormSubmit = async (values: typeof initialJobFormData) => {
    let geofence_coordinates: [number, number][] = [];

    if (values.custom_geofence === '1') {
      const geometry = values.geofence_features?.geometry;

      if ((geometry as any)?.coordinates[0]?.length) {
        geofence_coordinates = (geometry as any).coordinates[0].map((coordinate: [number, number]) => [
          coordinate[1],
          coordinate[0],
        ]);
      } else {
        toaster.error({
          description: 'Please draw a custom geofence on the map or select a circular geofence to proceed.',
        });
        return undefined;
      }
    }

    const postData: ISaveJobData = {
      id: values.id,
      name: values.name,
      job_code: values.job_code,
      description: values.description,
      users: values.users.length ? values.users.map((user: any) => user.userId) : [],
      requireGeofence: values.requireGeofence.length ? 1 : 0,
      rate: values.rate ? +values.rate : 0,
      track_mileage: values.track_mileage.length ? 1 : 0,
      radius: values.radius ? +values.radius : 0,
      contactRequired: values.contactRequired.length ? 1 : 0,
      contact_name: values.contact_name,
      contact_phone: values.contact_phone,
      contact_mobile: values.contact_mobile,
      contact_email: values.contact_email,
      landmark: '',
      street: values.street,
      state: values.state,
      city: values.city,
      zip_code: values.zip_code,
      country: values.country,
      on_arrival: values.on_arrival || '0',
      on_departure: values.on_departure || '0',
      latitude: values.latitude,
      longitude: values.longitude,
      all_users: values.all_users,
      geofenceMembers: values.geofenceMembers.length
        ? values.geofenceMembers.map((user: any) => ({ id: user.userId }))
        : [],
      custom_geofence: values.custom_geofence === '1' ? true : false,
      geofence_coordinates: geofence_coordinates,
      ...(customFieldsData ? { custom_fields: transformCustomFieldsToPostData(customFieldsData, values) } : {}),
    };

    mutateAsync(postData).then(() => {
      navigate(routePaths.jobsAndTasks.base.route);
    });
  };

  return (
    <Box>
      <Title titleProps={{ mb: '3' }} backBtnAfterOnClick={jobsTasksHasRedirected}>
        {formData.id ? t('jobsTasks:updateJob') : t('jobsTasks:addJob')}
      </Title>
      <Formik
        enableReinitialize
        initialValues={formData}
        validationSchema={jobFormValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {() => (
          <Form noValidate>
            <Tabs>
              <TabHeader tabHeaders={tabHeaders} />
              <TabPanels tabPanels={tabPanels} />
            </Tabs>
            <Box mt={3}>
              <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                {formData.id ? t('jobsTasks:updateJob') : t('jobsTasks:addJob')}
              </Button>
              <Button variant="outline" colorScheme="secondary" ml="5" onClick={() => navigate(-1)}>
                {t('jobsTasks:cancel')}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default JobFormTabs;
